import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  linkWithCredential,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  signOut,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { detachUserFromFamily } from '@coparent/shared/src/services/familyService';
import { readStoredAttribution } from '@coparent/shared/src/services/analyticsService';
import { buildSignupNotificationPrefs } from '@coparent/shared/src/utils/notificationPrefs';

const googleProvider = new GoogleAuthProvider();
const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');

// Sync sessionStorage adapter — readStoredAttribution accepts an async store,
// and sessionStorage's get/set are sync, so we wrap them.
const sessionStore = {
  get: async (key) => {
    try { return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(key) : null; }
    catch { return null; }
  },
  set: async (key, value) => {
    try { if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(key, value); }
    catch { /* private mode */ }
  },
};

const buildNewUserDoc = (user, displayName, signupPlatform = 'web', attribution = null, foundVia = null) => {
  const base = {
    email: user.email,
    displayName: displayName || user.displayName,
    photoURL: user.photoURL || null,
    familyIds: [],
    createdAt: new Date().toISOString(),
    signupPlatform,
    platforms: { [signupPlatform]: true },
    // Canonical signup defaults — single source of truth in
    // @coparent/shared/src/utils/notificationPrefs.
    notificationPrefs: buildSignupNotificationPrefs()
  };

  if (attribution && (attribution.utm_source || attribution.source)) {
    base.signupSource = attribution.source || attribution.utm_source || null;
    base.signupMedium = attribution.utm_medium || null;
    base.signupCampaign = attribution.utm_campaign || null;
    base.signupContent = attribution.utm_content || null;
    base.signupReferrer = attribution.referrer || null;
    base.signupFirstTouchAt = attribution.ts ? new Date(attribution.ts).toISOString() : null;
  }

  // Self-reported "how did you find us" answer from the signup screen. Optional,
  // so only stamped when the user actually picked something.
  if (foundVia) base.foundVia = foundVia;

  return base;
};

export const registerUser = async (email, password, displayName, attribution = null, foundVia = null) => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;

  await updateProfile(user, { displayName });

  // Write user doc to Firestore with a timeout so registration isn't blocked
  // if Firestore rules aren't configured yet
  const writePromise = setDoc(
    doc(db, 'users', user.uid),
    buildNewUserDoc(user, displayName, 'web', attribution, foundVia)
  );

  try {
    await Promise.race([
      writePromise,
      new Promise((_, reject) => setTimeout(() => reject(new Error('Firestore write timeout')), 5000))
    ]);
  } catch (firestoreError) {
    console.error('Firestore user doc write failed or timed out:', firestoreError);
  }

  return user;
};

export const loginUser = async (email, password) => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  return userCredential.user;
};

async function handleOAuthSignIn(provider, foundVia = null) {
  try {
    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    const userDoc = await getDoc(doc(db, 'users', user.uid));
    // Gate on `createdAt`, NOT `exists()`: a telemetry/state write can create a
    // bare users/{uid} doc before this runs, and an `exists()` guard would then
    // skip the profile write — leaving the user with no createdAt/email/
    // signupPlatform (invisible to the new-account rollup, no onboarding email).
    // Merge so identity fields fill onto any such stub without clobbering it.
    if (!userDoc.exists() || !userDoc.data()?.createdAt) {
      // OAuth runs in a popup — pull attribution from sessionStorage at the
      // moment of account creation. First-touch is what we want for marketing
      // attribution; the campaign that brought them in originally.
      const attribution = await readStoredAttribution({ storage: sessionStore });
      await setDoc(doc(db, 'users', user.uid), buildNewUserDoc(user, user.displayName, 'web', attribution, foundVia), { merge: true });
    }

    return user;
  } catch (error) {
    if (error.code === 'auth/account-exists-with-different-credential') {
      // If user is already signed in, link the new credential
      const credential = OAuthProvider.credentialFromError(error)
        || GoogleAuthProvider.credentialFromError(error);
      if (auth.currentUser && credential) {
        const result = await linkWithCredential(auth.currentUser, credential);
        return result.user;
      }
      throw new Error(
        'An account already exists with this email. Please sign in with your original method first, then link this provider from Settings.'
      );
    }
    throw error;
  }
}

export const signInWithGoogle = (foundVia = null) => handleOAuthSignIn(googleProvider, foundVia);

export const signInWithApple = (foundVia = null) => handleOAuthSignIn(appleProvider, foundVia);

export const logoutUser = async () => {
  await signOut(auth);
};

export const getPrimaryAuthProvider = () => {
  const user = auth.currentUser;
  if (!user) return null;
  const providers = (user.providerData || []).map((p) => p.providerId);
  if (providers.includes('password')) return 'password';
  if (providers.includes('apple.com')) return 'apple';
  if (providers.includes('google.com')) return 'google';
  return providers[0] || null;
};

/**
 * Re-authenticate the current user using the appropriate provider. Required
 * by Firebase before calling `user.delete()`. For password accounts the
 * caller must pass the current password; for OAuth providers the popup is
 * opened automatically.
 */
export const reauthenticateCurrentUser = async (password) => {
  const user = auth.currentUser;
  if (!user) throw new Error('No authenticated user');

  const provider = getPrimaryAuthProvider();
  if (provider === 'password') {
    if (!password) throw new Error('Password is required');
    const credential = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credential);
    return;
  }
  if (provider === 'google') {
    await reauthenticateWithPopup(user, googleProvider);
    return;
  }
  if (provider === 'apple') {
    await reauthenticateWithPopup(user, appleProvider);
    return;
  }
  throw new Error('Unsupported authentication provider for re-authentication');
};

/**
 * Permanently delete the current user. Requires a recent re-authentication;
 * see `reauthenticateCurrentUser`. Detaches the user from every family they
 * belong to, deletes the Firestore user doc, then deletes the Firebase Auth
 * user.
 */
export const deleteCurrentUser = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('No authenticated user');

  const userDocRef = doc(db, 'users', user.uid);
  let familyIds = [];
  try {
    const snap = await getDoc(userDocRef);
    if (snap.exists()) {
      familyIds = snap.data().familyIds || [];
    }
  } catch (err) {
    console.warn('Failed to read user doc before deletion', err);
  }

  for (const familyId of familyIds) {
    try {
      await detachUserFromFamily(familyId, user.uid);
    } catch (err) {
      console.error('Failed to detach user from family', familyId, err);
    }
  }

  try {
    await deleteDoc(userDocRef);
  } catch (err) {
    console.warn('Failed to delete user doc', err);
  }

  await user.delete();
};

export const getUserData = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error fetching user data:', error);
    return null;
  }
};
